import React, { useContext, useRef } from 'react'
import cx from 'classnames'
import omit from 'lodash/omit'

import Radio from '../../forms/Radio'
import RadioGroupContext from '../../forms/RadioGroup/RadioGroupContext'
import { Card } from '../Card'
import { SafeRadioOwnProps } from '../constants'
import { useFastClick } from '../hooks/useFastClick'
import { CardBlockElements, CardRadioItemProps } from '../interfaces'

import cm from './CardRadioItem.module.css'

/**
 * The **CardRadio** component extends the **Card** to render a Radio component within the card. **Notice that CardRadio should not be used without CardRadio.Group component**
 */
export const CardRadioBase = <T extends CardBlockElements = 'div'>({
  value,
  disabled: disabledProp,
  invalid: invalidProp,
  children,
  className,
  showControl = true,
  ...rest
}: CardRadioItemProps<T>) => {
  const { radioGroupValue, radioGroupInvalidState, radioGroupDisabledState } =
    useContext(RadioGroupContext) || {}
  const radioRef = useRef<HTMLInputElement | null>(null)
  const checked = radioGroupValue === value
  const invalid =
    radioGroupInvalidState === undefined ? invalidProp : radioGroupInvalidState || invalidProp
  const disabled =
    radioGroupDisabledState === undefined ? disabledProp : radioGroupDisabledState || disabledProp

  const { mouseDown, mouseUp } = useFastClick(radioRef, { disabled })

  const radio = (
    <Radio
      value={value}
      disabled={disabled}
      invalid={invalid}
      className={cx(cm.cardRadio__control, !showControl && 'visually-hidden')}
      label=""
      ariaLabel=""
      inputRef={radioRef}
    />
  )

  return (
    <Card
      {...omit(rest, SafeRadioOwnProps)}
      onMouseDown={mouseDown}
      onMouseUp={mouseUp}
      disabled={disabled}
      className={cx(
        cm.cardRadio,
        {
          [cm.disabled]: disabled,
          [cm.selected]: checked,
          [cm.invalid]: invalid,
        },
        className,
      )}
    >
      <div className={cm.cardRadio__content}>{children}</div>
      {radio}
    </Card>
  )
}
